/**
 * グローバル画像キャッシュ
 *
 * 同じ画像の重複フェッチを防ぎ、Blob URL を共有する。
 * 参照カウント方式でメモリリークを防止。
 */
class ImageCache {
  private cache = new Map<string, Promise<string>>()
  private refCounts = new Map<string, number>()

  /**
   * 画像 Blob URL を取得（キャッシュまたはフェッチ）
   *
   * @param key - 画像のキー（R2 object key）
   * @param fetcher - 画像をフェッチする関数
   * @returns Blob URL の Promise
   */
  async get(key: string, fetcher: () => Promise<string>): Promise<string> {
    // 既存のフェッチが進行中ならそれを返す（重複リクエスト防止）
    if (this.cache.has(key)) {
      this.refCounts.set(key, (this.refCounts.get(key) || 0) + 1)
      return this.cache.get(key)!
    }

    // 新規フェッチ開始
    const promise = fetcher()
    this.cache.set(key, promise)
    this.refCounts.set(key, 1)

    // エラー時はキャッシュから削除
    promise.catch(() => {
      this.cache.delete(key)
      this.refCounts.delete(key)
    })

    return promise
  }

  /**
   * 参照カウントを減らし、0 になったら Blob URL を解放
   *
   * @param key - 画像のキー
   */
  release(key: string): void {
    const count = this.refCounts.get(key) || 0

    if (count <= 1) {
      // 最後の参照 - Blob URL を解放
      const promise = this.cache.get(key)
      promise?.then(url => URL.revokeObjectURL(url))

      this.cache.delete(key)
      this.refCounts.delete(key)
    } else {
      this.refCounts.set(key, count - 1)
    }
  }

  /**
   * 全キャッシュをクリア
   */
  clear(): void {
    this.cache.forEach(promise => {
      promise.then(url => URL.revokeObjectURL(url))
    })
    this.cache.clear()
    this.refCounts.clear()
  }
}

export const imageCache = new ImageCache()
